import { useEffect, useMemo, useState } from "react";

import isEqual from "lodash/isEqual";
import keyBy from "lodash/keyBy";
import omit from "lodash/omit";
import sortBy from "lodash/sortBy";
import { useToasts } from "react-toast-notifications";

import { useUser } from "@components/user-context";
import { useUpdatePartnerConsent } from "@features/partner-consent/hooks/use-update-partner-consent";
import {
  FETCH_PARTNERS_APEX_CONSENT,
  UPSERT_APEX_SETTINGS,
} from "@gql/partner-consent";
import { useNorthbeamMutation, useNorthbeamQuery } from "@hooks/http";
import { useUserEnrichment } from "@hooks/http/use-user-enrichment";
import {
  type Partner,
  PartnerPermissionEnum,
  type UpsertApexSettings,
  type UpsertApexSettingsVariables,
} from "@north-beam/nb-client-side";
import {
  type FetchPartnersApexConsent_me_partnerApexConsent,
  type FetchPartnersApexConsent,
  type FetchPartnersApexConsent_me_apexSettings,
} from "@north-beam/nb-client-side/nb-api-graphql-generated";
import { PARTNERS_INFO_MAPPING } from "@north-beam/nb-common/constants";

export const ALLOWED_ATTRIBUTION_MODELS = [
  "northbeam_custom",
  "first_touch",
  "last_touch_non_direct",
  "last_touch",
  "linear",
] as const;

type AllowedAttributionModelsType = (typeof ALLOWED_ATTRIBUTION_MODELS)[number];
type ApexSettings = Omit<
  FetchPartnersApexConsent_me_apexSettings,
  "__typename"
>;

export const DEFAULT_ATTRIBUTION_MODEL = ALLOWED_ATTRIBUTION_MODELS[0];

const CASH_ATTRIBUTION_WINDOW_OPTION = {
  value: "infinity",
  label: "Infinite",
} as const;

const isAllowedModel = (modelName: string) =>
  ALLOWED_ATTRIBUTION_MODELS.some((allowedModel) => allowedModel === modelName);

export const useApexConsentForPartners = () => {
  const { addToast } = useToasts();
  const { user } = useUser();

  const { grantPartnerConsent, revokePartnerConsent, isUpdatingConsent } =
    useUpdatePartnerConsent({ permission: PartnerPermissionEnum.data_exports });

  const [apexConsentForPartners, setApexConsentForPartners] = useState<
    FetchPartnersApexConsent_me_partnerApexConsent[]
  >([]);

  const [currentApexSettings, setCurrentApexSettings] =
    useState<ApexSettings | null>(null);

  const [upsertApexSettings, { loading: isUpsertingApexSettings }] =
    useNorthbeamMutation<UpsertApexSettings, UpsertApexSettingsVariables>(
      UPSERT_APEX_SETTINGS,
      {
        doNotRaiseErrorOnFailure: true,
        onError: (error) =>
          addToast(
            `An error occurred when trying to update your Apex settings: ${error}`,
            { appearance: "error" },
          ),
      },
    );
  const { userEnrichment } = useUserEnrichment();

  const availableAttributionModelOptions = useMemo(() => {
    const modelsOptions =
      userEnrichment?.attribution.attributionModelOptions ?? [];

    const allowedOptions = modelsOptions.filter(
      (option) => isAllowedModel(option.attribution_model_name) && option.state,
    );

    const parsedOptions = allowedOptions.map((option) => {
      return {
        label: option.attribution_model_label,
        value: option.attribution_model_name as AllowedAttributionModelsType,
      };
    });

    const sortedOptions = sortBy(parsedOptions, "label");
    return sortedOptions;
  }, [userEnrichment?.attribution.attributionModelOptions]);

  const accrualAvailableAttributionWindowOptions = useMemo(() => {
    const windowsOptions =
      userEnrichment?.attribution.attributionWindowDaysOptions ?? [];

    const allowedOptions = windowsOptions.filter((option) => option.state);

    const parsedOptions = allowedOptions.map((option) => ({
      label: option.attribution_window_days_label,
      value: option.attribution_window_days,
    }));

    return parsedOptions;
  }, [userEnrichment?.attribution.attributionWindowDaysOptions]);

  const availableAttributionWindowOptions = {
    cash: [CASH_ATTRIBUTION_WINDOW_OPTION],
    accrual: accrualAvailableAttributionWindowOptions,
  };

  const {
    data,
    loading: isLoadingApexConsent,
    refetch: refetchPartnersApexConsent,
  } = useNorthbeamQuery<FetchPartnersApexConsent>(FETCH_PARTNERS_APEX_CONSENT, {
    doNotRaiseErrorOnFailure: true,
  });

  useEffect(() => {
    const partnerApexConsent = data?.me?.partnerApexConsent ?? [];
    setApexConsentForPartners(partnerApexConsent);

    const apexSettings = data?.me?.apexSettings ?? null;
    const apexSettingsWithoutTypename = apexSettings
      ? omit(apexSettings, "__typename")
      : null;

    setCurrentApexSettings(apexSettingsWithoutTypename);
  }, [data]);

  const getPartnerDisplayName = (partner: Partner) =>
    PARTNERS_INFO_MAPPING[partner].displayName;

  const onChangeConsent = (partner: Partner, updatedHasConsent: boolean) => {
    setApexConsentForPartners((prev) =>
      prev.map((partnerConsent) => {
        if (partnerConsent.partner !== partner) {
          return partnerConsent;
        }
        return {
          ...partnerConsent,
          hasConsent: updatedHasConsent,
        };
      }),
    );
  };

  const onSubmitUpdatedConsent = async (updatedSettings: ApexSettings) => {
    const currentApexConsentForPartners = data?.me?.partnerApexConsent ?? [];
    const currentApexConsentForPartnersMapping = keyBy(
      currentApexConsentForPartners,
      "partner",
    );

    const partnersWithConsentUpdated = apexConsentForPartners.filter(
      (updatedConsent) => {
        const currentConsent =
          currentApexConsentForPartnersMapping[updatedConsent.partner]
            ?.hasConsent;

        return updatedConsent.hasConsent !== currentConsent;
      },
    );

    const isSettingsUpdated = !isEqual(currentApexSettings, updatedSettings);
    const isConsentUpdated = partnersWithConsentUpdated.length > 0;
    if (!isConsentUpdated && !isSettingsUpdated) {
      return;
    }

    const partnersConsentUpdateMutations = partnersWithConsentUpdated.map(
      (partnerConsent) => {
        const consentUpdateMethod = partnerConsent.hasConsent
          ? grantPartnerConsent
          : revokePartnerConsent;

        return consentUpdateMethod(partnerConsent.partner);
      },
    );

    const settingsUpdateMutation = isSettingsUpdated
      ? upsertApexSettings({
          variables: updatedSettings,
        })
      : null;

    const allUpdateMutations = [
      ...partnersConsentUpdateMutations,
      settingsUpdateMutation,
    ];
    await Promise.all(allUpdateMutations);

    refetchPartnersApexConsent();

    const partnersGrantedConsent = partnersWithConsentUpdated
      .filter((partnerConsent) => partnerConsent.hasConsent)
      .map((partnerConsent) => getPartnerDisplayName(partnerConsent.partner));

    const partnersRevokedConsent = partnersWithConsentUpdated
      .filter((partnerConsent) => !partnerConsent.hasConsent)
      .map((partnerConsent) => getPartnerDisplayName(partnerConsent.partner));

    const apexSettingsUpdatedMessage = (
      <b>North Star Metric successfully updated!</b>
    );

    const apexConsentUpdatedMessage = (
      <>
        <b>Apex consent successfully updated!</b>
        {!!partnersGrantedConsent.length && (
          <span>
            Granted consent for: {partnersGrantedConsent.join(", ")}.{" "}
          </span>
        )}
        {!!partnersRevokedConsent.length && (
          <span>
            Revoked consent for: {partnersRevokedConsent.join(", ")}.{" "}
          </span>
        )}
      </>
    );

    addToast(
      <div className="flex flex-col gap-2">
        {isSettingsUpdated && (
          <>
            {apexSettingsUpdatedMessage}
            {isConsentUpdated && (
              <hr className="my-2 h-0.5 border-t-0 bg-alien-1" />
            )}
          </>
        )}
        {isConsentUpdated && apexConsentUpdatedMessage}
      </div>,
      { appearance: "success" },
    );
  };

  const canToggleConsent = user.isTrueAccountOwner;
  const isUpdatingApex = isUpdatingConsent || isUpsertingApexSettings;

  return {
    isLoadingApexConsent,
    apexConsentForPartners,
    currentApexSettings,
    onChangeConsent,
    getPartnerDisplayName,
    onSubmitUpdatedConsent,
    isUpdatingApex,
    availableAttributionModelOptions,
    availableAttributionWindowOptions,
    canToggleConsent,
  };
};
